import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Calendar, CheckCircle, Phone } from 'lucide-react';
import { HERO_DATA } from '../data/mockData';

const Booking = () => {
  const location = useLocation();
  const preSelectedService = location.state?.selectedService || '';

  useEffect(() => {
    // Load Square Appointments widget
    const script = document.createElement('script');
    script.src = 'https://square.site/appointments/buyer/widget/6wppctrgz6dajl/LKSWMR94XM4QH.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      // Cleanup script on unmount
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#111216] pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-b from-[#1A1A1F] to-[#111216]">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Book Your <span className="text-[#B8860B]">Appointment</span>
          </h1>
          <p className="text-gray-400 text-lg max-w-3xl mx-auto leading-relaxed mb-8">
            Select your preferred service, date, and time. Our Square booking system makes scheduling easy and secure.
          </p>
          
          {preSelectedService && (
            <div className="inline-flex items-center space-x-2 bg-[#B8860B]/20 border border-[#B8860B]/50 rounded-full px-6 py-3">
              <CheckCircle className="w-5 h-5 text-[#B8860B]" />
              <span className="text-white font-semibold">Pre-selected: {preSelectedService}</span>
            </div>
          )}
        </div>
      </section>

      {/* Square Booking Widget */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            {/* Trust Badges */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 text-center">
                <div className="w-12 h-12 rounded-full bg-[#B8860B]/20 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-6 h-6 text-[#B8860B]" />
                </div>
                <h3 className="text-white font-semibold mb-2">Secure Booking</h3>
                <p className="text-gray-400 text-sm">Powered by Square</p>
              </div>
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 text-center">
                <div className="w-12 h-12 rounded-full bg-[#B8860B]/20 flex items-center justify-center mx-auto mb-4">
                  <Calendar className="w-6 h-6 text-[#B8860B]" />
                </div>
                <h3 className="text-white font-semibold mb-2">Instant Confirmation</h3>
                <p className="text-gray-400 text-sm">Receive confirmation immediately</p>
              </div>
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 text-center">
                <div className="w-12 h-12 rounded-full bg-[#B8860B]/20 flex items-center justify-center mx-auto mb-4">
                  <Phone className="w-6 h-6 text-[#B8860B]" />
                </div>
                <h3 className="text-white font-semibold mb-2">Easy Management</h3>
                <p className="text-gray-400 text-sm">Modify or cancel anytime</p>
              </div>
            </div>

            {/* Square Widget Container */}
            <div className="bg-white/5 backdrop-blur-sm border border-[#B8860B]/30 rounded-3xl p-8 min-h-[600px]">
              <div id="square-appointments-widget"></div>
            </div>

            {/* Help Section */}
            <div className="mt-12 text-center">
              <p className="text-gray-400 mb-4">
                Need help with booking? Give us a call!
              </p>
              <a href={`tel:${HERO_DATA.phone.replace(/[^0-9]/g, '')}`}>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-2 border-[#B8860B] text-[#B8860B] hover:bg-[#B8860B] hover:text-[#111216] font-semibold"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Call {HERO_DATA.phone}
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Info Section */}
      <section className="py-20 bg-gradient-to-b from-[#111216] to-[#1A1A1F]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-8 text-center">
              What to <span className="text-[#B8860B]">Expect</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                {
                  title: 'Select Your Service',
                  description: 'Choose from our range of detailing services and packages'
                },
                {
                  title: 'Pick Date & Time',
                  description: 'Select a convenient date and time slot that works for you'
                },
                {
                  title: 'Provide Vehicle Info',
                  description: 'Tell us about your vehicle so we can prepare accordingly'
                },
                {
                  title: 'Get Confirmation',
                  description: 'Receive instant confirmation and appointment details via email'
                }
              ].map((item, index) => (
                <div
                  key={item.title}
                  className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6"
                >
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 rounded-full bg-[#B8860B] text-[#111216] font-bold flex items-center justify-center flex-shrink-0">
                      {index + 1}
                    </div>
                    <div>
                      <h3 className="text-white font-semibold text-lg mb-2">{item.title}</h3>
                      <p className="text-gray-400 text-sm">{item.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Booking;
